<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import { hasNextPage } from "@/utils/paginationUtils/paginationUtils";
    import QuestionsEmptyListIcon from "@apps/reviews/components/icons/QuestionsEmptyListIcon.svelte";
    import QuestionsList from "@apps/reviews/components/QuestionsList/QuestionsList.svelte";
    import ReviewsTheme from "@apps/reviews/components/ReviewsTheme/ReviewsTheme.svelte";
    import { SCROLL_QUESTIONS_TAB } from "@apps/reviews/constants";
    import QuestionsQuery from "@apps/reviews/queries/QuestionsQuery";
    import QuestionsService from "@apps/reviews/services/QuestionsService";
    import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
    import { questionsFormModalToken } from "@apps/reviews/tokens";
    import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
    import ScrollManager from "@lib/ScrollManager/ScrollManager";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import Observable from "@ui-utils/Observable.svelte";
    import { lastValueFrom } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import Button from "@components/Buttons/Button.svelte";
    import EmptyState from "@components/EmptyState/EmptyState.svelte";
    import Icon from "@components/Icon.svelte";
    import LoadMore from "@components/LoadMore.svelte";
    import { ModalManager } from "@components/Modal/ModalManager";
    import T from "@components/T.svelte";

    export let reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    export let questionsQuery: QuestionsQuery;
    export let questionsService: QuestionsService;
    export let productIdManager: ProductIdManagerInterface;
    export let modalManager: ModalManager;
    export let i18nModule: I18nModule;
    export let showAddQuestionButton: boolean;
    export let showAddQuestionButtonInEmptyList: boolean;
    export let isNestedWidget: boolean;
    export let afterWidgetInject: () => void;

    const scrollManager = container.resolve(ScrollManager);
    let widgetRef: HTMLElement;

    const reviewsSettings$ =
        reviewsStorefrontValuesService.selectReviewsSettings();
    const reviewsBranding$ =
        reviewsStorefrontValuesService.selectReviewsBrandingOptions();

    const productId$ = productIdManager.productId$;
    const i18n = i18nModule.exports.i18n;

    const questions$ = questionsQuery.all$;
    const questionsPaginationMeta$ = questionsQuery.paginationMeta$;
    const questionsStatus$ = questionsQuery.status$;

    onMount(() => {
        if (!isNestedWidget) {
            scrollManager.register(SCROLL_QUESTIONS_TAB, widgetRef);
        }
        getQuestions();
        afterWidgetInject();
    });

    const getQuestions = () => {
        if ($productId$ === null) {
            throw new Error("Product ID is not defined");
        }

        if ($questionsStatus$ === STATUS.VIRGIN) {
            lastValueFrom(
                questionsService.getQuestionsByProductId({
                    productId: $productId$,
                })
            );
        }
    };

    const handleQuestionsLoadMore = () => {
        if ($productId$ === null) {
            throw new Error("Product ID is not defined");
        }

        lastValueFrom(
            questionsService.loadMoreQuestionsByProductId({
                productId: $productId$,
                offset:
                    $questionsPaginationMeta$.perPage +
                    $questionsPaginationMeta$.currentOffset,
            })
        );
    };

    const handleAddQuestion = () => {
        if ($productId$ === null) {
            throw new Error("Product ID is not defined");
        }

        modalManager.openModal(questionsFormModalToken, undefined, {
            productId: $productId$,
        });
    };
</script>

<div bind:this={widgetRef} class="gw-rv-questions-list-widget">
    {#if $reviewsSettings$ && $reviewsBranding$}
        <ReviewsTheme branding={$reviewsBranding$}>
            <div class="gw-rv-questions-list-header">
                <div class="gw-rv-questions-list-header__left" />
                <div class="gw-rv-questions-list-header__right">
                    {#if showAddQuestionButton}
                        <Button
                            variant={"opacityOnHover"}
                            size="large"
                            on:click={handleAddQuestion}
                        >
                            <span>
                                <T
                                    namespace="reviews.critical.ReviewsProductPageWidget"
                                    translationKey="ask_a_question"
                                />
                            </span>
                        </Button>
                    {/if}
                </div>
            </div>

            {#if $questions$.length > 0}
                <QuestionsList
                    questions={$questions$}
                    namespace="reviews.ReviewsProductPageWidget"
                />
                {#if hasNextPage($questionsPaginationMeta$)}
                    <div class="gw-rv-questions-list-widget__load-more-wrapper">
                        <Observable
                            observable={i18n.selectT(
                                "reviews.ReviewsProductPageWidget",
                                "load_more"
                            )}
                            let:value={t_load_more}
                        >
                            <Observable
                                observable={i18n.selectT(
                                    "reviews.ReviewsProductPageWidget",
                                    "load_more_aria_label"
                                )}
                                let:value={t_load_more_aria_label}
                            >
                                <LoadMore
                                    variant="opacityOnHover"
                                    text={t_load_more}
                                    ariaLabel={t_load_more_aria_label}
                                    loading={$questionsStatus$ ===
                                        STATUS.LOADING}
                                    on:loadMore={handleQuestionsLoadMore}
                                />
                            </Observable>
                        </Observable>
                    </div>
                {/if}
            {:else}
                <EmptyState>
                    <Icon
                        width={100}
                        height={80}
                        viewBox="0 0 100 84"
                        slot="icon"
                    >
                        <QuestionsEmptyListIcon />
                    </Icon>
                    <p slot="title" class="gw-rv-questions-empty-list__title">
                        <T
                            namespace="reviews.ReviewsProductPageWidget"
                            translationKey="no_questions_message"
                        />
                    </p>
                    <svelte:fragment slot="footer">
                        {#if showAddQuestionButtonInEmptyList}
                            <Button
                                class="gw-rv-questions-empty-list__button"
                                variant="link"
                                on:click={handleAddQuestion}
                                size="large"
                            >
                                <T
                                    namespace="reviews.critical.ReviewsProductPageWidget"
                                    translationKey="ask_a_question"
                                />
                            </Button>
                        {/if}
                    </svelte:fragment>
                </EmptyState>
            {/if}
        </ReviewsTheme>
    {/if}
</div>

<style lang="postcss" global>
    @import "../../../../themes/default/default.css";
    @import "../../../../themes/globals.css";

    .gw-rv-questions-list-widget {
        &__load-more-wrapper {
            margin-top: 1em;
        }
    }

    .gw-rv-questions-list-header {
        position: relative;
        display: flex;
        justify-content: space-between;
        margin-bottom: 20px;
        align-items: center;
        overflow: visible;

        &__left {
            align-items: center;
            display: flex;
            gap: 5px;
        }
        &__right {
            align-items: center;
            display: flex;
            gap: 5px;
        }
    }
    .gw-rv-reviews-list-filter {
        margin-bottom: 20px;
    }

    .gw-rv-questions-empty-list__title {
        color: #979faf;
        margin: 0;
    }

    :global(.gw-btn.gw-button.gw-rv-questions-empty-list__button) {
        color: #3772ff;
    }
</style>
